import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { PageLoader } from '../components/LoadingSpinner';
import { ArrowLeft, ChevronRight, DollarSign } from 'lucide-react';

export const SalesPage = () => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // Allow sellers with any form of active subscription (subscription_active, Gold, Elite, Creator)
    const canAccessSales = user?.subscription_active || user?.membership_status === 'gold' || 
                           user?.membership_status === 'elite' || user?.membership_status === 'Creator' ||
                           user?.is_seller;
    if (!canAccessSales) {
      navigate('/subscription');
      return;
    }
    fetchSales();
  }, [isAuthenticated, user]);

  const fetchSales = async () => {
    try {
      const response = await api().get('/sales');
      setSales(response.data);
    } catch (error) {
      console.error('Failed to fetch sales:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <PageLoader />;

  const totalEarnings = sales
    .filter(s => s.status === 'paid')
    .reduce((sum, s) => sum + s.item_price, 0);

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading font-bold text-4xl uppercase text-white">My Sales</h1>
          <div className="stat-card px-6 py-3">
            <p className="text-xs text-zinc-500">Total Earnings</p>
            <p className="font-mono text-2xl font-bold text-emerald-400">${totalEarnings.toFixed(2)}</p>
          </div>
        </div>
        
        {sales.length === 0 ? (
          <div className="stat-card p-8 text-center">
            <DollarSign className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No sales yet</p>
            <Link to="/sell" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">
              List your first card
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {sales.map((sale) => (
              <Link
                key={sale.id}
                to={`/orders/${sale.id}`}
                className="stat-card p-4 flex items-center gap-4 hover:border-white/10 transition-colors"
                data-testid={`sale-${sale.id}`}
              >
                <div className="w-16 h-20 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                  {sale.card_details?.image_url ? (
                    <img src={sale.card_details.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600 font-heading font-bold">
                      {sale.card_details?.player_name?.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{sale.card_details?.player_name}</p>
                  <p className="text-sm text-zinc-400">{sale.card_details?.year} {sale.card_details?.set_name}</p>
                  <span className={`badge mt-2 ${sale.status === 'paid' ? 'badge-emerald' : 'badge-amber'}`}>
                    {sale.status}
                  </span>
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold text-emerald-400">${sale.item_price?.toFixed(2)}</p>
                  <p className="text-xs text-zinc-500">You earned</p>
                  <ChevronRight className="w-5 h-5 text-zinc-500 ml-auto mt-2" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SalesPage;
